/**
 * What a member sees and controls about themselves — `MEM-005` … `MEM-010`.
 *
 * Registration (`member-registration.ts`) is how these fields are first collected; this module is
 * how they are read back and changed afterwards, which is a different surface with different
 * rules. The registration wizard writes step by step and refuses once `status === 'active'`; My
 * Account and the preference screens write whole screens, once, when the member presses Save.
 *
 * ## Why the copy lives here
 *
 * `apps/web` has no DOM test renderer (`apps/web/package.json` runs `tsx --test` over plain Node),
 * so a screen's wording can only be asserted where it is a value rather than JSX. Every heading,
 * helper line and button label an acceptance criterion quotes is therefore a constant in this
 * file, imported by the screen and by its test. The rule is the one
 * `member-matching-fields.ts` already follows: copy an AC quotes is data, not markup.
 *
 * ## What is deliberately not here
 *
 * - **The matches-per-event cap.** `MEM-009` lists it under Out of scope, and its own open
 *   question explains why it cannot be a profile control at all: the member's preference may only
 *   *lower* an event's number, so the control needs the event's setting and the effective cap is
 *   resolved at generation time. The prototype's fixed Up to 3 / 5 / 8 is wrong and is not built.
 * - **Social comfort and connection format option labels.** Both are taxonomy vocabularies
 *   (`social-comfort`, `connection-format`) whose terms an administrator authors under `ADM-002`.
 *   The screen renders `displayName` from the taxonomy; hard-coding `MEM-009` AC-5/AC-6's labels
 *   here would fork an admin-editable list. See {@link MEMBER_SINGLE_SELECT_VOCABULARIES}.
 * - **The bidirectional opt-out statement.** It is `platform-config`'s fixed text and is
 *   re-exported, never restated — see {@link BIDIRECTIONAL_OPT_OUT_STATEMENT}.
 */
import { z } from 'zod';
import { emailAddressField } from './email-address.js';
import { MEMBER_REGISTRATION_FIELD_LIMITS } from './member-registration.js';
/* ============================================================================
   The bidirectional opt-out statement
   ============================================================================ */
/**
 * The bidirectional opt-out (`ADM-019` AC-6, `MEM-010` AC-3). **Not a toggle — a statement.**
 *
 * This is the definition; `apps/api/src/domains/platform-config/types.ts` re-exports it so the
 * admin console's matching screen and the member's privacy screen render one string. It moved
 * here when `MEM-010` needed it on a member surface: the constant lived in an API domain served
 * only by `/api/platform-config`, which is behind `requireAdminGroup` plus the
 * `administer-platform-content` capability, so a member app could not reach it. Copying the
 * sentence into a member component was the other option, and `docs/plans/stage-m1-member-registration.md`
 * rules it out in as many words — it "must be rendered from there rather than retyped".
 *
 * Disabling the rule would breach a member's privacy election, so it is not representable: there
 * is no field for it on the platform configuration record, only this line rendered where a toggle
 * would otherwise sit.
 */
export const BIDIRECTIONAL_OPT_OUT_STATEMENT = 'A member who opts out of matching is excluded in both directions, always. This is not configurable.';
/* ============================================================================
   Identity, bio and the account screen  (`MEM-007`)
   ============================================================================ */
/** The bio's ceiling. Longer than the conversation starter's 120 because this is the paragraph the
 * prototype's Biography textarea holds, not a one-liner. */
export const MEMBER_BIO_MAX_LENGTH = 600;
/**
 * What My Account saves, in **one operation** (`MEM-007` AC-5).
 *
 * Every field on the screen is present on every save, which is what makes AC-5's "validates every
 * field on the screen and saves the whole set in one operation" true and AC-4's Cancel mean
 * something: a partial save is not expressible, so a half-finished screen cannot be half-committed.
 *
 * The email address is **not** here. It is its own request with its own verification round trip
 * ({@link memberEmailChangeRequestSchema}) because changing it is not the same kind of act as
 * changing a job title — see `MEM-007` AC-2.
 */
export const memberAccountSaveSchema = z
    .object({
    firstName: z.string().trim().min(1).max(MEMBER_REGISTRATION_FIELD_LIMITS.firstName.max),
    lastName: z.string().trim().min(1).max(MEMBER_REGISTRATION_FIELD_LIMITS.lastName.max),
    role: z.string().trim().min(1).max(MEMBER_REGISTRATION_FIELD_LIMITS.role.max),
    company: z.string().trim().min(1).max(MEMBER_REGISTRATION_FIELD_LIMITS.company.max),
    /** `MEM-007` AC-3 — optional, starts empty, and edited here rather than at registration. An
     * empty string is an erase and is accepted for the same reason `optionalTermId` accepts one. */
    bio: z.string().trim().max(MEMBER_BIO_MAX_LENGTH).optional(),
})
    .strict();
/** `MEM-007` AC-2. The new address only; the old one stays authoritative until the link is used. */
export const memberEmailChangeRequestSchema = z
    .object({ emailAddress: emailAddressField })
    .strict();
/* ============================================================================
   Social channels  (`MEM-008`)
   ============================================================================ */
/**
 * The four channels `MEM-008` AC-1 names, in the prototype's own order.
 *
 * `linkedin` is first and is **the one registration already collects** (`MEM-003` step 4), so a
 * member who filled it in during registration finds it here rather than being asked twice.
 *
 * Each carries the prototype's sub-line and the shape a handle takes, which is what
 * {@link normalizeSocialChannel} needs to turn `@deb.h` into a URL somebody can tap.
 */
export const MEMBER_SOCIAL_CHANNELS = [
    {
        id: 'linkedin',
        label: 'LinkedIn',
        // Registration's own line for this field (`MEM-003` step 4), reused because it is the same
        // field and a member should not be told two different things about it.
        hint: 'The profile people will look you up on',
        placeholder: 'linkedin.com/in/yourname',
        /** Prefix a bare handle gets. `null` where a handle is not a meaningful input (a website). */
        handlePrefix: 'https://www.linkedin.com/in/',
        host: 'linkedin.com',
    },
    {
        id: 'x',
        label: 'X',
        hint: 'Your handle, with or without the @',
        placeholder: '@yourname',
        handlePrefix: 'https://x.com/',
        host: 'x.com',
    },
    {
        id: 'instagram',
        label: 'Instagram',
        hint: 'Your handle, with or without the @',
        placeholder: '@yourname',
        handlePrefix: 'https://www.instagram.com/',
        host: 'instagram.com',
    },
    {
        id: 'website',
        label: 'Website',
        hint: 'Anything you would rather people saw first',
        placeholder: 'yourname.co',
        handlePrefix: null,
        host: null,
    },
];
export const MEMBER_SOCIAL_CHANNEL_IDS = MEMBER_SOCIAL_CHANNELS.map((channel) => channel.id);
export function memberSocialChannel(id) {
    const found = MEMBER_SOCIAL_CHANNELS.find((channel) => channel.id === id);
    if (found === undefined)
        throw new Error(`Unknown social channel "${id}".`);
    return found;
}
/** How long a stored channel value may be. The same ceiling registration puts on `linkedIn`. */
export const MEMBER_SOCIAL_CHANNEL_MAX_LENGTH = MEMBER_REGISTRATION_FIELD_LIMITS.linkedIn.max;
/**
 * A URL or a handle in, a URL out — `MEM-008` AC-4, "normalised on save, as the organizer website
 * field is".
 *
 * Three inputs a member actually types, all of which have to work:
 *
 *   - `https://x.com/deb` — already a URL, left alone but for trimming.
 *   - `x.com/deb` — a URL missing its scheme, which gets `https://`.
 *   - `@deb` or `deb` — a handle, which gets the channel's prefix. A website has no handle form
 *     (`handlePrefix: null`), so a bare `deb` there is treated as a hostname and gets `https://`.
 *
 * Returns `null` for anything empty, which is what makes AC-3's "independently removable" a
 * consequence of the same code path rather than a second one: clearing the field *is* the removal.
 */
export function normalizeSocialChannel(id, raw) {
    const value = (raw ?? '').trim();
    if (value === '')
        return null;
    if (/^https?:\/\//iu.test(value))
        return value;
    const channel = memberSocialChannel(id);
    const looksLikeDomain = /^[a-z0-9-]+(\.[a-z0-9-]+)+(\/|$)/iu.test(value);
    if (looksLikeDomain)
        return `https://${value.replace(/^\/+/u, '')}`;
    if (channel.handlePrefix === null) {
        // A website with no dot in it is not a hostname and not a handle. Left as the member typed it
        // rather than guessed at — a wrong guess here renders a link that goes nowhere.
        return value;
    }
    return `${channel.handlePrefix}${value.replace(/^@+/u, '')}`;
}
/**
 * The whole set, saved together. Every channel is present on every save; `null` clears one.
 *
 * AC-3's "each channel is optional and independently removable" falls out of this rather than
 * needing a delete endpoint per channel — the screen sends what it has, and a cleared field
 * arrives as `null`.
 */
export const memberSocialChannelsSaveSchema = z
    .object(Object.fromEntries(MEMBER_SOCIAL_CHANNEL_IDS.map((id) => [
    id,
    z.string().trim().max(MEMBER_SOCIAL_CHANNEL_MAX_LENGTH).nullable().optional(),
])))
    .strict();
/* ============================================================================
   Matching preferences  (`MEM-009`)
   ============================================================================ */
/**
 * The two member vocabularies that are **single-select with exactly three terms** — `MEM-009`
 * AC-5 and AC-6.
 *
 * Named here so the screen and its test agree about which controls are radio groups, without
 * either of them holding the option labels: those are `displayName`s an administrator authors
 * (`ADM-002`), served by `GET /api/members/registration/vocabularies`, and `SELECTION_RULES` in
 * the API's taxonomy domain is what makes them single-select for a member.
 *
 * **The seeded terms and the story's wording differ slightly and the taxonomy wins.** AC-5 says
 * "Appreciate a warm intro" where the seed says "May appreciate a warm intro"; AC-6 says
 * "1:1 introductions" where the seed says "1:1". Rendering the story's strings would fork a list
 * the console can edit, and the next administrator to rename a term would find one screen ignoring
 * them. Recorded on the pull request as a deliberate divergence.
 */
export const MEMBER_SINGLE_SELECT_VOCABULARIES = {
    socialComfort: 'social-comfort',
    connectionFormat: 'connection-format',
};
/**
 * Everything the matching-preferences screen saves, in one operation.
 *
 * `connectionFormatTermId` is **required and non-empty** — `MEM-009` AC-6's "exactly one always
 * selected". A member with no preferred format is a member the matching model has nothing to
 * place, and making the field nullable is exactly how that state arrives. Every other input may
 * be cleared.
 */
export const memberMatchingPreferencesSaveSchema = z
    .object({
    interestTagTermIds: z.array(z.string().min(1)),
    currentGoal: z
        .string()
        .trim()
        .max(MEMBER_REGISTRATION_FIELD_LIMITS.currentGoal.max)
        .optional(),
    connectionIntentTermIds: z.array(z.string().min(1)),
    connectionFormatTermId: z.string().trim().min(1),
    socialComfortTermId: z.string().trim().max(120).optional(),
    /** `MEM-009`'s open question decided it belongs here: it is collected at registration, it
     * changes, and it is a matching input. */
    careerStageTermId: z.string().trim().max(120).optional(),
    industryTermId: z.string().trim().max(120).optional(),
    conversationStarter: z
        .string()
        .trim()
        .max(MEMBER_REGISTRATION_FIELD_LIMITS.conversationStarter.max)
        .optional(),
})
    .strict();
/* ============================================================================
   Privacy elections  (`MEM-010`)
   ============================================================================ */
/**
 * The three elections, editable at any time (`MEM-010` AC-1).
 *
 * Required booleans rather than optional ones, for `privacyAnswersSchema`'s reason: an election
 * nobody made is not the same as an election made `false`, and this screen always renders all
 * three, so it always sends all three.
 */
export const memberPrivacyElectionsSaveSchema = z
    .object({
    appearInAttendeePreview: z.boolean(),
    receivePreEventMatchSuggestions: z.boolean(),
    receivePostEventFollowUpNotifications: z.boolean(),
})
    .strict();
/**
 * The three elections as the screen draws them, in the prototype's order.
 *
 * (a) and (b) are the two toggles the prototype's PROFILE screen carries; (c) is the third the
 * registration step collects and the prototype's profile omits. All three are here because
 * `MEM-010` AC-1 says all three are editable, and hiding the one the prototype forgot would make
 * a member's only notification election unreachable (AC-5, AC-6).
 */
export const MEMBER_PRIVACY_ELECTIONS = [
    {
        id: 'appearInAttendeePreview',
        label: 'Show me in attendee preview',
        description: 'Members see you 72h before an event',
        /** `MEM-010` AC-2. Said on the screen because the distinction is not obvious from the label,
         * and a member who thinks this hides the roster from them will not use it.
         *
         * Its second sentence was *"You can always see who is coming."* until Sumit's ruling of
         * 2026-09-11 put the roster behind a registration (`MEM-011` AC-1), which made it false for a
         * member who has not reserved. What is still true — and what the election does not change — is
         * that it never hides the list from the member who turns it off. */
        note: "This is about whether others see you. Turning it off never hides who's coming from you once you're registered.",
    },
    {
        id: 'receivePreEventMatchSuggestions',
        label: 'Pre-event match suggestions',
        description: 'Curated people to meet, 48h before',
        note: null,
    },
    {
        id: 'receivePostEventFollowUpNotifications',
        label: 'Post-event follow-up reminders',
        description: 'A nudge to follow up on who you met',
        note: null,
    },
];
/**
 * `MEM-010` AC-6 — the member's notification surface is exactly one election.
 *
 * Rendered under (c) so the smallness is visible rather than merely true. Everything else about
 * what the platform sends is a platform decision made in the admin console (`ADM-024`), which is
 * why `MEM-PRO-020` keeps categories away from members.
 */
export const MEMBER_NOTIFICATION_SCOPE_NOTE = 'This is your only notification setting. What else H2HSpark sends — a ticket, an event change, a connection request — is transactional and cannot be turned off.';
/* ============================================================================
   What needs a member profile row  (`MEM-049`)
   ============================================================================ */
/**
 * **Every method on `MemberProfileService`, and whether its request needs a member profile.**
 *
 * `MEM-049` AC-3, and the reason that criterion exists: `/members/me/profile` answers 404 until a
 * member profile row exists, and so does every other call that begins `await this.mustFind(userId)`.
 * A member who signed up and stopped before the profile questions has a real account — they can
 * sign in, change their password, hold tickets, ask to be deleted — and My Account was drawing
 * them a screenful of controls whose requests all answered 404.
 *
 * That was fixed twice and was still wrong. Both fixes were correct about the control in front of
 * them and neither asked the general question, because the answer lived nowhere: the screen and
 * the service each decided separately what needs a profile, which is two lists that can disagree,
 * and they disagreed twice.
 *
 * **So the answer lives here, once, and both sides read it.**
 *
 *   - `apps/api` is checked against it. `what-needs-a-member-profile-is-read-off-the-service.test.ts`
 *     parses `profile-service.ts`, derives this table from the class body — every non-private
 *     method, and whether its body calls `this.mustFind` — and asserts the derivation equals this
 *     object exactly. A method that gains `mustFind`, loses it, is added or is renamed fails that
 *     test, which is a failed build rather than a stranded member.
 *   - `apps/web` reads it. `MEMBER_ACCOUNT_CONTROL_READS` in the members slice says which method
 *     each control on My Account calls — a fact about the control, which is the screen's own to
 *     know — and `memberAccountControlWorksWithoutProfile` looks the answer up here. The screen
 *     carries no copy of the answer, so it cannot hold a stale one. The method names are typed, so
 *     a rename fails `tsc` in the web app as well.
 *
 * **This is a `Record` of the whole surface, not a set of the profile-requiring half.** A set can
 * be silently short: a method added without an entry is simply absent from it, which is exactly
 * how three settings rows and a biography got left behind. Naming every method means a new one
 * has to be classified before the build goes green.
 */
export const MEMBER_PROFILE_SERVICE_NEEDS_PROFILE = {
    /* MEM-005 — the profile and its tiles. */
    readOwnProfile: true,
    readStats: false,
    listEventsAttended: false,
    /* MEM-006 — the photo. */
    readPhotoObjectKey: true,
    attachPhoto: true,
    detachPhoto: true,
    notifyPhotoRemoved: false,
    /* MEM-007 — My Account's own save, and the email change. */
    saveAccount: true,
    requestEmailChange: true,
    /**
     * **False, and not by accident.** The link is minted against the row and consumed against the
     * account; the method reads the credential link rather than the profile, so it has never called
     * `mustFind`. It is unreachable without `requestEmailChange` having succeeded first, which does.
     */
    confirmEmailChange: false,
    /* USR-011 — the deletion request, and the way back out of it. */
    readDeletionRequest: false,
    requestAccountDeletion: false,
    cancelAccountDeletionRequest: false,
    /* MEM-008 — social channels. */
    readSocialChannels: true,
    saveSocialChannels: true,
    /* MEM-009 — matching preferences. */
    readMatchingPreferences: true,
    readRetiredSelections: true,
    saveMatchingPreferences: true,
    /* MEM-010 — privacy elections. */
    readPrivacyElections: true,
    savePrivacyElections: true,
};
/**
 * Does this call need a member profile row to succeed?
 *
 * A function rather than a bare index, so callers get `boolean` instead of the literal type the
 * `as const` table carries — a screen branching on it must not have its branch folded away.
 */
export function memberProfileServiceNeedsProfile(method) {
    return MEMBER_PROFILE_SERVICE_NEEDS_PROFILE[method];
}
/* ============================================================================
   Screen copy the acceptance criteria quote
   ============================================================================ */
/**
 * Headings, helper lines and button labels, from `Member App.dc.html`'s ACCOUNT EDIT, SOCIAL
 * CHANNELS SETTINGS and MATCHING PREFERENCES sections, and from `Web App.dc.html` §2.15.
 *
 * Quoted strings in an acceptance criterion are the criterion, so they live where a test can read
 * them. Where the prototype and a story disagree the story wins on behaviour and the prototype
 * wins on wording — `docs/standards/building-from-designs.md`'s rule.
 *
 * **`MEMBER_PROFILE_COPY` stood above this and is gone** (2026-09-12). Every string in it belonged
 * to `/app/profile` and to nothing else: *How others see you* and *Only you can see this*
 * (`MEM-005` AC-5's visible seam), *Your interests*, *About* and its empty line, and the
 * *My Account* link out. The screen was removed on Sumit's ruling and `Web App.dc.html` §2.15 has
 * no band, no interests block and no link out to draw them on, so the words went with the screen
 * rather than being rehomed onto a card that does not exist. The biography's own copy is
 * `bioHeading` / `bioPlaceholder` below, which the account card has used since Phase 5.
 */
export const MEMBER_ACCOUNT_COPY = {
    /**
     * **"Your account", not "My Account"** — `Web App.dc.html` §2.15's own h1, adopted in Phase 5
     * when the profile and the account hub merged into one screen.
     *
     * It was *My Account* because the 17 August phone prototype wrote it that way. The nav avatar
     * and the in-app footer have both said *Your account* since Phase 1, so the app was calling one
     * destination two things; no criterion fixes either wording, and presentation is the
     * prototype's.
     */
    title: 'Your account',
    /** §2.15's lede, under the title. */
    blurb: 'Manage your profile, matching preferences, and connected channels.',
    /**
     * §2.15's profile card, which is the merge itself: the photo, the identity fields, the email
     * address and the bio in one card, where they were four cards and two screens.
     */
    profileCardHeading: 'Your profile',
    /** §2.15's *Account Management* card, over the settings rows (§3.20). */
    accountManagementHeading: 'Account Management',
    /** §2.15's *Your registrations* card. `MEM-022` AC-6 fixes both of these strings. */
    registrationsHeading: 'Your registrations',
    registrationsBlurb: "Events you've registered for, with receipts.",
    photoHeading: 'Profile photo',
    photoHint: 'JPG or PNG, at least 400×400px.',
    photoUpload: 'Upload photo',
    /** §2.15's own label, on the merged profile card where a photo is usually already there. */
    photoChange: 'Change photo',
    photoRemove: 'Remove photo',
    identityHeading: 'Account information',
    emailHeading: 'Email address',
    /** `MEM-007` AC-2 — said before the member types, not after they submit. */
    emailChangeHint: 'We will send a link to the new address. Until you use it, you keep signing in with the address you have now.',
    emailChangeAction: 'Change email',
    /**
     * `USR-003` AC-5's entry point, on the row that shows the address (`MEM-007` AC-2's card).
     *
     * The one place the member app admits an address is unproved. It reads as an offer rather than
     * an instruction because verification blocks nothing — see `MEMBER_VERIFY_EMAIL_COPY`'s header.
     */
    emailVerifyAction: 'Verify it',
    /** `MEM-007` AC-7. One sentence for both outcomes — see {@link MemberEmailChangeAccepted}. */
    emailChangeNotice: 'If that address can be used, we have sent a link to it. Check the inbox.',
    securityHeading: 'Security & connections',
    bioHeading: 'Biography',
    bioPlaceholder: 'Tell members a little about yourself…',
    /** `MEM-007` AC-4 — the pinned footer's two controls. */
    cancel: 'Cancel',
    save: 'Save changes',
    saved: 'Saved.',
    /** Shown when Cancel would discard something. AC-4's "nothing saves implicitly" is only
     * trustworthy if leaving is also explicit. */
    discardPrompt: 'Discard your changes?',
    /**
     * **Ending the session on this device** (`USR-007` AC-3).
     *
     * The member app shipped with no sign-out control of any kind: the one that existed lived on
     * the old `/app/profile` account form and went with it when `MEM-005` replaced that screen.
     * `USR-007` AC-3 — *"A member ends the session on the device they are using"* — was true of the
     * API and false of every screen a member could reach.
     *
     * The wording says **this device**, because that is the whole distinction AC-3 draws against
     * AC-4's central revocation. A member signing out of a shared laptop should not have to wonder
     * whether their phone just lost its session too.
     */
    /**
     * When the account cannot be loaded at all — a failure, not the ordinary "no profile yet" state,
     * which renders an empty profile to fill in instead. It says what happened and offers the one
     * action that helps; the screen it replaces was a skeleton that waited forever, and because sign
     * out lives on this page it left a member no way out either.
     */
    /**
     * Named `couldNotLoad` rather than anything with "unavailable" in it, deliberately.
     * `member-identity-screens.test.ts` forbids `alreadyExists` / `taken` / `inUse` / `unavailable`
     * / `conflict` anywhere in My Account's source, so the screen cannot grow a branch on whether
     * an email address is already registered (`MEM-007` AC-7, account enumeration). This string is
     * about the account failing to load and has nothing to do with an address — but the guard is a
     * substring scan, and a word that means two things on one screen is worth renaming rather than
     * excusing.
     */
    couldNotLoad: "We couldn't load your account just now. Your details are safe — this is us, not you.",
    couldNotLoadRetry: 'Try again',
    /**
     * The fallback ask, for the moment before `useMemberAppAdmission` answers. The server owns the
     * real wording — one ask, rendered by the You tab and by My Account — and this is only so the
     * card is never blank while that request is in flight.
     */
    profileNotFinished: 'Your member profile is not finished yet, so your details and email address are edited there rather than here.',
    profileNotFinishedAction: 'Finish my member profile',
    signOutHeading: 'Session',
    /**
     * **Longer than §2.15's line, and the extra clause is the criterion** (`USR-007` AC-3).
     *
     * The prototype writes *"You'll be signed out on this device only."* — which says the right
     * thing and leaves the other half to be inferred. AC-3 draws its whole distinction against
     * AC-4's central revocation, and a member signing out of a shared laptop should not have to
     * wonder whether their phone just lost its session too. Same shape as `passwordSub` below,
     * where the prototype's sentence is also true and also short of the criterion.
     */
    signOutSub: 'Signs you out on this device only. Your other devices stay signed in.',
    /**
     * **`Web App.dc.html` §2.15's own word**, adopted 2026-09-12. It read *Sign out*, which was the
     * 17 August phone prototype's; the in-app footer has said *Log out* since Phase 1, so one
     * control had two names in one app. No criterion quotes either — `USR-007` AC-3 fixes what the
     * control does — and presentation is the prototype's.
     */
    signOut: 'Log out',
    /** The prototype's own sentence on the Change password screen, kept verbatim. */
    passwordHeading: 'Change password',
    passwordSub: 'Choose a strong password you don’t use elsewhere. You’ll stay signed in on this device.',
    /**
     * **The half the prototype's sentence leaves the member to infer** (`USR-004` AC-3, `MSG-005`).
     *
     * AC-3 is *"the change ends every other session in the same action"*, and `MSG-005`'s
     * confirmation email describes exactly that. The prototype writes only *"You'll stay signed in
     * on this device"*, which implies the rest without stating it — and the thing being implied is
     * the entire reason a member changes a password after losing a phone. A member who is not told
     * their other devices were signed out cannot tell whether the action they took did the job they
     * took it for.
     *
     * So this is added beside the prototype's sentence rather than replacing it: same card, same
     * position, one more line. Recorded as a deliberate copy addition in the pull request rather
     * than in `handoff-corrections.md`, which is for where a prototype is *wrong* — this one is not
     * wrong, it is silent on a behaviour that shipped after it was drawn.
     */
    passwordEndsOtherSessions: 'Your other devices will be signed out.',
    /**
     * The way back from the Change password page to Your account (`MEM-057`-era Phase 5).
     *
     * §2.12, §2.13 and §2.14 all draw this link and label it with the account screen's own title,
     * so the fourth sub-page uses the same one rather than a fourth wording.
     */
    backToAccount: 'Your account',
    /**
     * **Asking to be deleted, and being able to take it back** (`USR-011` AC-1, AC-3, AC-7).
     *
     * ## Every sentence here is AC-7, and AC-7 is one decision made once
     *
     * *"Nothing the member is told promises erasure that has not happened: the acknowledgement
     * email and every screen state that access has ended and the request is recorded, never that
     * data has been deleted."* ADR 030: **no member data is purged in MVP.** The account moves to
     * `deletion_requested` and later to `deleted`, access ends and the personal fields stop being
     * served, and no row is removed.
     *
     * So the words below are *request*, *recorded*, *reviewed*, *access ends*. Not deleted, erased,
     * removed, wiped, permanently, or forever. A soft delete is a legitimate MVP position; a soft
     * delete described as an erasure is not, and the story says exactly how thin the line is —
     * *"the two are separated by one email's wording"*. It is separated by this screen's wording
     * too, and the screen is read by more members than the email.
     *
     * ## No date, because there is no date
     *
     * `SYS-008`'s "30-day obligation" is a title whose body never states one, and nothing in MVP
     * turns on it: there is no automated purge for a clock to bind. Copy promising thirty days
     * would be the platform inventing a commitment out of a heading, and the first person to
     * discover it was invented would be a member at day thirty-one.
     *
     * ## The prototype does not draw this screen
     *
     * There is no deletion or danger-zone surface anywhere in `Member App.dc.html` — the nearest
     * thing is the Disconnect confirmation, which is about a connection. So this section is built
     * from the story, in the shape of the `Session` card beside it, and it is named as an addition
     * in the pull request rather than as a divergence in `handoff-corrections.md`.
     */
    deletionHeading: 'Delete this account',
    deletionSub: 'Ask us to close your account. Someone at H2HSpark reviews the request and ends your access.',
    deletionAction: 'Request account deletion',
    /** The confirm step. A mis-tap here should cost a second tap, not an account. */
    deletionConfirmPrompt: 'Request deletion of this account?',
    deletionConfirmBody: 'We will record your request and someone will review it. You keep your access until then, and you can withdraw the request at any time.',
    deletionConfirm: 'Yes, request deletion',
    /** What the card says once a request is outstanding (AC-3). */
    deletionPendingHeading: 'Deletion requested',
    deletionPendingSub: 'We have your request and it is waiting to be reviewed. You can still use your account, and you can change your mind.',
    deletionCancelAction: 'Withdraw this request',
    deletionCancelled: 'Your deletion request has been withdrawn.',
};
export const MEMBER_SOCIAL_CHANNELS_COPY = {
    title: 'Social channels',
    /** The prototype's own sentence, which is also `MEM-008` AC-2's rule stated to the member. */
    blurb: 'Connect your channels so the people you meet can find you. Shown on your profile only to accepted connections.',
    /**
     * `MEM-008`'s note: a member with no channel has accepted a connection and given the other
     * person no way to reach them anywhere else. Worth saying on the screen.
     *
     * **Reworded 2026-09-12 with ADR 052.** It said *"H2HSpark has no inbox, so a connection with no
     * channel has no way to reach you"*, which was true under ADR 032 and is not now: a connection
     * can message this member here. What is still true, and is the reason `MEM-008` wanted a
     * sentence at all, is that a channel is how somebody reaches them **off** the platform — which
     * is where a conversation usually goes once it is real.
     */
    emptyWarning: 'You have not added any. A connection can message you here, and without a channel they have no way to reach you anywhere else.',
    save: 'Save channels',
    saved: 'Saved.',
};
export const MEMBER_MATCHING_PREFERENCES_COPY = {
    title: 'Matching preferences',
    /** The prototype's blurb, which carries `MEM-009` AC-7's required sentence in its own words. */
    blurb: 'These are the inputs we use to suggest who you should meet. Update them anytime — changes apply to your next event.',
    interestsHeading: 'Interests',
    /** `MEM-009` AC-2, verbatim. */
    interestsHint: 'Pick 3–5. Shared interests are the strongest signal.',
    goalHeading: 'Current goal',
    /** `MEM-009` AC-3, verbatim. */
    goalHint: 'I am currently building / exploring / seeking…',
    intentHeading: 'Connection intent',
    /** `MEM-009` AC-4, verbatim. */
    intentHint: 'What are you looking for right now? Pick all that apply.',
    comfortHeading: 'Social comfort level',
    /** `MEM-009` AC-5, verbatim. */
    comfortHint: "We'll match you into settings that feel right.",
    formatHeading: 'Preferred connection format',
    formatHint: 'Exactly one, always — it is what the matching model places you with.',
    save: 'Save preferences',
    /** `MEM-009` AC-7. Repeated beside the button because that is where a member is deciding
     * whether the change will affect tonight. */
    savedNotice: 'Saved. Changes apply to your next event.',
};
export const MEMBER_PRIVACY_COPY = {
    title: 'Privacy',
    blurb: 'Three choices about how visible you are. Change them whenever you like.',
    save: 'Save privacy settings',
    saved: 'Saved.',
};
/** `MEM-009` AC-7's sentence, isolated because two screens render it and a test asserts it. */
export const MEMBER_PREFERENCES_APPLY_NOTICE = 'changes apply to your next event';
/**
 * The Verify your email screen (`USR-003` AC-5), and the sentences it says in each of the four
 * states a member can arrive in.
 *
 * ## Why the copy is here rather than in the screen
 *
 * The same reason every other constant in this file is: an acceptance criterion that quotes a
 * sentence is only testable if the sentence is a value. `USR-003` AC-5 requires the screen to
 * *"state the position"* and offer a resend, and "states the position" is a claim about wording.
 *
 * ## The position, and why it is three sentences rather than a warning
 *
 * Verification is **non-blocking at registration and gating at first use** (ADR 028,
 * `MEM-AUTH-005` / `MEM-AUTH-006`). A member whose address is unproved has a real, working
 * account; what they do not have is an address that counts as evidence of ownership. A screen
 * that said "verify your email to continue" would be describing a product we deliberately did
 * not build, and a member who believed it would stop half way through a registration that was
 * never blocked.
 *
 * ## Why a wrong code and a spent one say the same thing
 *
 * They are the same answer from the identity provider. A code that has been consumed and a code
 * that was never issued both come back as `AUTH_INVALID_CODE`, so the screen cannot tell them
 * apart and must not pretend to. One sentence covers both and names the resend as the way out —
 * which is also the enumeration-safe shape, since a screen that could say "that code was already
 * used" would be confirming that a code had been issued.
 */
export const MEMBER_VERIFY_EMAIL_COPY = {
    title: 'Verify your email',
    /**
     * The position, first sentence: what is already true. Said before anything is asked of the
     * member, because it is the part they are most likely to be wrong about.
     */
    positionAccountWorks: 'Your account is set up and you can keep using it. Nothing here is blocking you.',
    /** The position, second sentence: what verifying is actually for. */
    positionWhatItUnlocks: 'Verifying proves the address is yours. Until you do, it does not count as proof of who you are — so signing in with Google will make a second account instead of joining this one, and an organizer who registered you by email cannot hand that registration over.',
    /** The position, third sentence: where the code came from and what to do with it. */
    positionWhereTheCodeIs: 'We emailed a code to the address below. Enter it here, or open the link in that email.',
    codeLabel: 'Verification code',
    submit: 'Verify my email',
    submitting: 'Verifying…',
    resend: 'Send a new code',
    resending: 'Sending…',
    /**
     * `ORG-004` AC-5's sentence, deliberately reused word for word — the organizer's resend already
     * says it, and a member reading the same product should not meet a second phrasing of one fact.
     * The second half is the invalidation, said plainly because a member holding two emails needs to
     * know which one to open.
     */
    resendNotice: 'Sent. Check your inbox — and your spam folder. The code in the newest email is the only one that works now.',
    /** 410 `AUTH_FLOW_EXPIRED`. */
    expired: 'That code has expired. Send a new one and use the code in the newest email.',
    /**
     * 400 `AUTH_INVALID_CODE` — a wrong code, a spent one and a **superseded** one are all the same
     * answer, and the sentence names all three causes without saying which applied. The third was
     * confirmed against the sandbox pool on 2026-09-01 (`USR-003` AC-5): a code a resend had replaced
     * comes back through this branch. The vagueness is deliberate — distinguishing them would
     * confirm that a code had been issued to the address. See the header.
     */
    refused: 'That code did not work. It may have been mistyped, already used, or replaced by a newer one. Send a new code and use the code in the newest email.',
    /**
     * The end state's heading, over {@link verified} on the member app's confirmation card (TW
     * 44261258: Brad chose the reservation's confirmed layout, "Go with Option A", 2026-10-01).
     */
    verifiedHeading: 'Your email is verified',
    /** The end state. Also what a member sees who arrives here already verified. */
    verified: 'This address is now proof that the account is yours.',
    /** The link back, so the screen is not a dead end. */
    back: 'Back to My Account',
    // ── The other way in: a mailed `?token=` link (`ADM-039` AC-3, ruling 83) ────────────────────
    //
    // An administrator can send a member the ordinary address-verification mail from the member
    // record. That mail carries a **credential link** rather than a code (ADR 042) — the same
    // `address-proof` credential the organizer's confirmation link is, minted by the same
    // `issueVerificationLink` and spent by the same `POST /organizers/onboarding/email-confirmations`
    // — so it lands on this screen with a `?token=` and none of the four sentences above apply. The
    // member may well be signed out: the token *is* the credential and the endpoint takes no
    // session.
    //
    // **One refusal, and the API is why.** That endpoint answers `{ confirmed: boolean }` and
    // nothing else. An expired link, a link already spent, a link superseded by a newer one and a
    // link whose address has since moved are one answer, deliberately: distinguishing them would let
    // somebody probe which accounts exist by guessing tokens. An address that is *already verified*
    // is not a fourth answer either — a live link over a verified address simply confirms again and
    // succeeds, and a dead one over a verified address is the same `false` as any other dead link.
    // So `linkRefused` names the causes without claiming which applied, exactly as `refused` above
    // does for the code, and the screen must not invent a distinction the API refuses to make.
    /**
     * What the member reads before pressing. **The press is the point**: the page does not spend the
     * token on load, because a mail client or a link scanner that prefetches URLs would consume a
     * single-use credential before the member ever saw the page, and the verification would appear
     * to have happened by itself.
     */
    linkIntro: 'Press the button to confirm that the address this email was sent to is yours. Nothing has changed until you do.',
    /** The one refusal the endpoint's one boolean supports. See the block comment above. */
    linkRefused: 'That link no longer works. It may have expired, been used already, or been replaced by a newer one. Nothing about your account has changed — sign in and send a new one from My Account.',
    /** A link that arrived without its token at all: a truncated paste, or a stripped query string. */
    linkMissing: 'That link is missing its code. Sign in and send a new one from My Account, or ask the person who sent it to send it again.',
    /** The way on for somebody who followed the link with no session in this browser. */
    linkSignIn: 'Go to sign in',
};
/**
 * The query parameter the Verify your email screen reads a code out of, so a mailed **link** lands
 * a member on a pre-filled form rather than on an empty one.
 *
 * **Something composes such a link as of 2026-09-01, and this comment used to say nothing did.**
 * The pool's own verification message carries a bare code; turning it into a link needed a
 * `CustomMessage` trigger discriminating `CustomMessage_VerifyUserAttribute` from
 * `CustomMessage_ForgotPassword` — the two share one message template, so rewriting the template
 * in place would have rewritten password-recovery mail too. That trigger was built on 2026-08-31
 * (`infrastructure/aws/cdk/lambda/cognito-custom-message.ts`) and deployed and smoked on the
 * sandbox on 2026-09-01, where a mailed link landed a member here with the code pre-filled and
 * verified the address. Exactly as this comment predicted, **nothing in `apps/web` moved for it**:
 * the screen already read the parameter, so it was a pool change and not also a client change.
 */
export const MEMBER_VERIFY_EMAIL_CODE_PARAM = 'code';
/**
 * The query parameter the same screen reads a **credential link** out of (`ADM-039` AC-3).
 *
 * Beside `MEMBER_VERIFY_EMAIL_CODE_PARAM` rather than instead of it, because the two are different
 * credentials arriving at one address. `?code=` is the identity provider's six digits, spent
 * against the caller's own session; `?token=` is a `<rowId>.<secret>` this platform minted
 * (ADR 042), spent against nothing but itself. A member holds whichever their mail carried, and
 * the screen branches on which parameter is present rather than on who is signed in.
 *
 * Spelled `token` because that is what `OnboardingService.memberVerificationLink` composes and
 * what the organizer's confirmation link has used since `ORG-004`. The two surfaces share one
 * mint, one lifetime and one confirmation route; sharing the parameter name is the cheapest way to
 * keep them from drifting.
 */
export const MEMBER_VERIFY_EMAIL_TOKEN_PARAM = 'token';
//# sourceMappingURL=member-profile.js.map